/**
 * HeaderNavigation Web Component
 * Manages header navigation, gender tabs, mobile menu, search modal, and mega menu
 * Follows Shopify Dawn theme architecture patterns
 */
class HeaderNavigation extends HTMLElement {
  constructor() {
    super();

    // Page gender from data attribute (women | men | unisex | undefined)
    this.pageGender = document.body?.dataset?.pageGender;

    // Cache DOM elements
    this.cacheElements();

    // Initialize components
    this.init();
  }

  /**
   * Cache all DOM element references to avoid repeated queries
   */
  cacheElements() {
    this.elements = {
      // Header
      header: this.querySelector('#mainHeader'),

      // Desktop gender tabs
      buttonHome: this.querySelector('#buttonHome'),
      buttonMan: this.querySelector('#buttonMan'),
      buttonWoman: this.querySelector('#buttonWoman'),

      // Desktop menus
      menuMan: this.querySelector('#menuMan'),
      menuWoman: this.querySelector('#menuWoman'),

      // Footer sections
      footerMan: document.querySelector('#footerMan'),
      footerWoman: document.querySelector('#footerWoman'),

      // Mobile navigation
      hamburger: this.querySelector('#hamburgerBtn'),
      mobileDrawer: this.querySelector('#mobileNavDrawer'),
      mobileOverlay: this.querySelector('#mobileNavOverlay'),
      mobileClose: this.querySelector('#mobileNavClose'),
      mobileButtonHome: this.querySelector('#mobileButtonHome'),
      mobileButtonMan: this.querySelector('#mobileButtonMan'),
      mobileButtonWoman: this.querySelector('#mobileButtonWoman'),
      mobileMenuMan: this.querySelector('#mobileMenuMan'),
      mobileMenuWoman: this.querySelector('#mobileMenuWoman'),

      // Mobile search
      mobileSearchBtn: this.querySelector('#mobileSearchBtn'),
      mobileSearchModal: this.querySelector('#mobileSearchModal'),
      mobileSearchOverlay: this.querySelector('#mobileSearchOverlay'),
      mobileSearchClose: this.querySelector('#mobileSearchClose'),
      mobileSearchInput: this.querySelector('#mobileSearchModal #searchResultsMobile'),

      // Mega menu
      headerBottom: this.querySelector('.header-bottom'),
      megaPanels: this.querySelectorAll('.mega-panel'),
    };

    // Bound handler references for cleanup
    this.boundHandlers = {
      scroll: this.handleScroll.bind(this),
      resize: this.updateHeaderHeight.bind(this),
      documentClick: this.handleDocumentClick.bind(this),
      keydown: this.handleKeydown.bind(this),
    };
  }

  /**
   * Initialize all header functionality
   */
  init() {
    this.updateHeaderHeight();
    this.setupStickyHeader();
    this.setupMobileNavigation();
    this.setupMobileSearch();
    this.initializeGenderTabs();
    this.setupGenderTabListeners();
    this.setupMegaMenu();

    window.addEventListener('resize', this.boundHandlers.resize, { passive: true });

    // Enable mobile drawer transitions after load
    if (this.elements.mobileDrawer) {
      setTimeout(() => {
        this.elements.mobileDrawer.classList.add('mobile-nav-loaded');
      }, HEADER_TIMING.TRANSITION_DELAY);
    }
  }

  /**
   * Lifecycle: Called when element is added to DOM
   */
  connectedCallback() {
    // Event listeners are added in init()
  }

  /**
   * Lifecycle: Called when element is removed from DOM
   */
  disconnectedCallback() {
    // Cleanup event listeners
    window.removeEventListener('scroll', this.boundHandlers.scroll);
    window.removeEventListener('resize', this.boundHandlers.resize);
    document.removeEventListener('click', this.boundHandlers.documentClick);
    document.removeEventListener('keydown', this.boundHandlers.keydown);
  }

  /**
   * Setup sticky header on scroll
   */
  setupStickyHeader() {
    if (!this.elements.header) return;

    window.addEventListener('scroll', this.boundHandlers.scroll, { passive: true });
    this.handleScroll(); // Initial check
  }

  /**
   * Handle scroll event for sticky header
   */
  handleScroll() {
    if (!this.elements.header) return;
    this.elements.header.classList.toggle('scrolled', window.scrollY > HEADER_TIMING.STICKY_THRESHOLD);
  }

  /**
   * Update CSS variable for current header height
   */
  updateHeaderHeight() {
    if (!this.elements.header) return;
    document.documentElement.style.setProperty('--site-header-height', `${this.elements.header.offsetHeight - 6}px`);
  }

  /**
   * Setup mobile navigation drawer
   */
  setupMobileNavigation() {
    const { hamburger, mobileDrawer, mobileClose, mobileOverlay } = this.elements;

    if (!hamburger || !mobileDrawer) return;

    hamburger.addEventListener('click', () => this.openMobileNav());
    mobileClose?.addEventListener('click', () => this.closeMobileNav());

    // Close on overlay or drawer background click
    mobileDrawer.addEventListener('click', (e) => {
      if (e.target === mobileDrawer || e.target === mobileOverlay) {
        this.closeMobileNav();
      }
    });

    // Close when tapping any mobile nav link
    mobileDrawer.addEventListener('click', (e) => {
      if (e.target.closest('.mobile-menu-link, .mobile-submenu-link, .mobile-user-link')) {
        this.closeMobileNav();
      }
    }, true);
  }

  /**
   * Open mobile navigation drawer
   */
  openMobileNav() {
    const { mobileDrawer, hamburger } = this.elements;
    if (!mobileDrawer || !hamburger) return;

    mobileDrawer.classList.add('active');
    hamburger.classList.add('active');
    hamburger.setAttribute('aria-expanded', 'true');
    mobileDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Trap focus in mobile drawer
    const focusElement = this.elements.mobileClose || mobileDrawer;
    trapFocus(mobileDrawer, focusElement);
  }

  /**
   * Close mobile navigation drawer
   */
  closeMobileNav() {
    const { mobileDrawer, hamburger } = this.elements;
    if (!mobileDrawer || !hamburger) return;

    mobileDrawer.classList.remove('active');
    hamburger.classList.remove('active');
    hamburger.setAttribute('aria-expanded', 'false');
    mobileDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Remove focus trap and return focus to trigger
    removeTrapFocus();
    hamburger.focus();
  }

  /**
   * Setup mobile search modal
   */
  setupMobileSearch() {
    const { mobileSearchBtn, mobileSearchModal, mobileSearchClose, mobileSearchOverlay } = this.elements;

    if (!mobileSearchBtn || !mobileSearchModal) return;

    mobileSearchBtn.addEventListener('click', () => this.openMobileSearch());
    mobileSearchClose?.addEventListener('click', () => this.closeMobileSearch());

    // Close on overlay click
    mobileSearchModal.addEventListener('click', (e) => {
      if (e.target === mobileSearchModal || e.target === mobileSearchOverlay) {
        this.closeMobileSearch();
      }
    });
  }

  /**
   * Open mobile search modal
   */
  openMobileSearch() {
    const { mobileSearchModal, mobileSearchInput } = this.elements;
    if (!mobileSearchModal) return;

    mobileSearchModal.classList.add('active');
    mobileSearchModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus input after animation
    if (mobileSearchInput) {
      setTimeout(() => mobileSearchInput.focus(), HEADER_TIMING.FOCUS_DELAY);
    }

    // Trap focus in modal
    trapFocus(mobileSearchModal, mobileSearchInput);
  }

  /**
   * Close mobile search modal
   */
  closeMobileSearch() {
    const { mobileSearchModal, mobileSearchBtn } = this.elements;
    if (!mobileSearchModal) return;

    mobileSearchModal.classList.remove('active');
    mobileSearchModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Remove focus trap and return focus to trigger
    removeTrapFocus();
    mobileSearchBtn?.focus();
  }

  /**
   * Initialize gender tabs based on page gender or saved preference
   */
  initializeGenderTabs() {
    let activeGender;
    let noActiveState = false;

    if (this.pageGender) {
      // Page has explicit gender (women | men | unisex)
      if (this.pageGender === 'unisex') {
        // Unisex pages: use saved preference or default to men
        activeGender = StorageUtility.getGenderPreference() || 'man';
      } else {
        // Explicit gender page: use page gender
        activeGender = this.pageGender;

        // Keep storage in sync (but only for explicit gendered pages)
        const storageValue = this.pageGender === 'women' ? 'woman' : 'man';
        const savedGender = StorageUtility.getGenderPreference();
        if (savedGender !== storageValue) {
          StorageUtility.setGenderPreference(storageValue);
        }
      }
    } else {
      // Homepage (no page gender) - show men's menu by default but no active tab
      activeGender = 'man';
      noActiveState = true;
    }

    const isWomen = activeGender === 'women' || activeGender === 'woman';
    this.setGenderUI(isWomen, noActiveState);
  }

  /**
   * Setup gender tab click listeners
   */
  setupGenderTabListeners() {
    document.addEventListener('click', this.boundHandlers.documentClick, true);
    document.addEventListener('keydown', this.boundHandlers.keydown);
  }

  /**
   * Handle document-level click events (gender tabs, mega menu close)
   */
  handleDocumentClick(e) {
    const btn = e.target.closest('button');
    if (!btn) {
      // Close mega menu if clicking outside header-bottom
      if (this.elements.headerBottom && !this.elements.headerBottom.contains(e.target)) {
        this.closeMegaMenu();
      }
      return;
    }

    // HOME button
    if (btn.id === 'buttonHome' || btn.id === 'mobileButtonHome') {
      e.preventDefault();
      StorageUtility.remove('headerTab');
      setTimeout(() => this.navigateToHome(), HEADER_TIMING.NAVIGATION_DELAY);
      return;
    }

    // WOMEN button
    if (btn.id === 'buttonWoman' || btn.id === 'mobileButtonWoman') {
      e.preventDefault();
      StorageUtility.setGenderPreference('woman');

      if (this.pageGender !== 'women') {
        setTimeout(() => this.navigateToWomen(), HEADER_TIMING.NAVIGATION_DELAY);
      } else {
        this.setGenderUI(true);
      }
      return;
    }

    // MEN button
    if (btn.id === 'buttonMan' || btn.id === 'mobileButtonMan') {
      e.preventDefault();
      StorageUtility.setGenderPreference('man');

      if (this.pageGender !== 'men') {
        setTimeout(() => this.navigateToMen(), HEADER_TIMING.NAVIGATION_DELAY);
      } else {
        this.setGenderUI(false);
      }
      return;
    }
  }

  /**
   * Handle keydown events (ESC to close modals/menus)
   */
  handleKeydown(e) {
    if (e.key !== 'Escape') return;

    // Close mobile nav if open
    if (this.elements.mobileDrawer?.classList.contains('active')) {
      this.closeMobileNav();
      return;
    }

    // Close mobile search if open
    if (this.elements.mobileSearchModal?.classList.contains('active')) {
      this.closeMobileSearch();
      return;
    }

    // Close mega menu if open
    if (this.elements.headerBottom?.classList.contains('mega-open')) {
      this.closeMegaMenu();
    }
  }

  /**
   * Update gender-specific UI elements (menus, buttons, footer)
   * @param {boolean} isWomen - True to show women's content
   * @param {boolean} noActive - If true, prevents adding 'active' class to tabs
   */
  setGenderUI(isWomen, noActive = false) {
    const {
      buttonMan, buttonWoman,
      menuMan, menuWoman,
      footerMan, footerWoman,
      mobileButtonMan, mobileButtonWoman,
      mobileMenuMan, mobileMenuWoman,
    } = this.elements;

    // Helper to show/hide menus and update button states
    const updateElements = (menuShow, menuHide, btnActive, btnInactive) => {
      // Show/hide menus
      if (menuShow) menuShow.style.display = 'block';
      if (menuHide) menuHide.style.display = 'none';

      // Update button active states
      if (noActive) {
        // No active state for unisex/homepage
        btnActive?.classList.remove('active');
        btnInactive?.classList.remove('active');
      } else {
        btnActive?.classList.add('active');
        btnInactive?.classList.remove('active');
      }
    };

    // Update desktop elements
    updateElements(
      isWomen ? menuWoman : menuMan,
      isWomen ? menuMan : menuWoman,
      isWomen ? buttonWoman : buttonMan,
      isWomen ? buttonMan : buttonWoman
    );

    // Update footer elements
    updateElements(
      isWomen ? footerWoman : footerMan,
      isWomen ? footerMan : footerWoman,
      null,
      null
    );

    // Update mobile elements
    updateElements(
      isWomen ? mobileMenuWoman : mobileMenuMan,
      isWomen ? mobileMenuMan : mobileMenuWoman,
      isWomen ? mobileButtonWoman : mobileButtonMan,
      isWomen ? mobileButtonMan : mobileButtonWoman
    );
  }

  /**
   * Get current language code from URL
   * @returns {string} Two-letter language code or empty string
   */
  getLanguage() {
    const match = location.pathname.match(/^\/([a-z]{2})(\/|$)/);
    return match ? match[1] : '';
  }

  /**
   * Navigate to homepage
   */
  navigateToHome() {
    const lang = this.getLanguage();
    const homeUrl = document.body.dataset.homeUrl || (lang ? `/${lang}` : '/');
    location.href = homeUrl;
  }

  /**
   * Navigate to women's page
   */
  navigateToWomen() {
    const womenUrl = document.body.dataset.womenUrl;
    if (womenUrl) {
      location.href = womenUrl;
    } else {
      // Fallback to default path
      const lang = this.getLanguage();
      location.href = lang ? `/${lang}/pages/dames` : '/pages/dames';
    }
  }

  /**
   * Navigate to men's page
   */
  navigateToMen() {
    const menUrl = document.body.dataset.menUrl;
    if (menUrl) {
      location.href = menUrl;
    } else {
      // Fallback to default path
      const lang = this.getLanguage();
      location.href = lang ? `/${lang}/pages/heren` : '/pages/heren';
    }
  }

  /**
   * Setup mega menu hover and focus interactions
   */
  setupMegaMenu() {
    const root = this.elements.headerBottom;
    if (!root) return;

    let closeTimeout = null;

    // Show mega menu on hover
    root.addEventListener('mouseover', (e) => {
      const link = e.target.closest('[data-mega-target]');
      if (link && root.contains(link)) {
        this.showMegaMenu(link.dataset.megaTarget);
      }
    });

    // Close mega menu on mouse leave (with delay)
    root.addEventListener('mouseleave', () => {
      clearTimeout(closeTimeout);
      closeTimeout = setTimeout(() => this.closeMegaMenu(), HEADER_TIMING.MEGAMENU_CLOSE_DELAY);
    });

    // Show mega menu on focus
    root.addEventListener('focusin', (e) => {
      const link = e.target.closest('[data-mega-target]');
      if (link) {
        clearTimeout(closeTimeout);
        this.showMegaMenu(link.dataset.megaTarget);
      }
    });

    // Store close timeout for cleanup
    this.megaMenuCloseTimeout = closeTimeout;
  }

  /**
   * Show specific mega menu panel
   * @param {string} key - The mega menu key to show
   */
  showMegaMenu(key) {
    const root = this.elements.headerBottom;
    if (!root) return;

    clearTimeout(this.megaMenuCloseTimeout);

    root.classList.add('mega-open');
    root.dataset.open = key;

    // Show/hide panels
    this.elements.megaPanels.forEach(panel => {
      panel.hidden = (panel.id !== `mega-${key}`);
    });

    // Update aria-expanded on nav links
    root.querySelectorAll('[data-mega-target]').forEach(link => {
      const isExpanded = link.dataset.megaTarget === key;
      link.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    });
  }

  /**
   * Close all mega menu panels
   */
  closeMegaMenu() {
    const root = this.elements.headerBottom;
    if (!root) return;

    root.classList.remove('mega-open');
    delete root.dataset.open;

    // Hide all panels
    this.elements.megaPanels.forEach(panel => {
      panel.hidden = true;
    });

    // Update aria-expanded on nav links
    root.querySelectorAll('[data-mega-target]').forEach(link => {
      link.setAttribute('aria-expanded', 'false');
    });
  }
}

// Register custom element
customElements.define('header-navigation', HeaderNavigation);
